// <toggle-panels> — the By type / By source switcher on the revenue-breakdown
// card. Both panels are server-rendered up front; buttons carry
// data-toggle-show="name", panels data-toggle-panel="name", and this element
// flips [hidden] + aria-pressed. All styling stays in the markup via
// Tailwind's aria-pressed: variant, so the element carries no classes.
//
// Deliberately NOT URL state, unlike the table filters: both datasets are
// already on the page, and the canvases need JS anyway — a round trip to
// swap two pre-rendered panels buys nothing. Chart.js's ResizeObserver
// handles the hidden→visible transition, so the donut in the initially
// hidden panel sizes itself correctly on first reveal.

class TogglePanels extends HTMLElement {
	#abort: AbortController | null = null;

	connectedCallback() {
		this.#abort?.abort();
		this.#abort = new AbortController();

		this.addEventListener(
			"click",
			(event) => {
				const button = (event.target as Element).closest<HTMLElement>(
					"[data-toggle-show]",
				);
				// Guard against a nested <toggle-panels> claiming outer buttons.
				if (!button || button.closest("toggle-panels") !== this) return;
				this.#show(button.dataset.toggleShow ?? "");
			},
			{ signal: this.#abort.signal },
		);
	}

	disconnectedCallback() {
		this.#abort?.abort();
	}

	#show(name: string) {
		for (const button of this.querySelectorAll<HTMLElement>(
			"[data-toggle-show]",
		)) {
			if (button.closest("toggle-panels") !== this) continue;
			button.setAttribute(
				"aria-pressed",
				String(button.dataset.toggleShow === name),
			);
		}
		for (const panel of this.querySelectorAll<HTMLElement>(
			"[data-toggle-panel]",
		)) {
			if (panel.parentElement?.closest("toggle-panels") !== this) continue;
			panel.hidden = panel.dataset.togglePanel !== name;
		}
	}
}

if (!customElements.get("toggle-panels")) {
	customElements.define("toggle-panels", TogglePanels);
}
